import toast from 'react-hot-toast';

import { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import {
  initializeFavorites,
  setCurrentPrompt,
  setIsStudyMaterialLoading,
  setQuizResults,
  setSavedID,
  setSetName,
  setStudyFlashcards,
  setStudyQuestions,
  setAssistivContent,
  setActiveAssistivTab,
  appendAssistivContent,
  setUploadedDocument,
  setDocumentUrl,
  setCourseDocumentId,
} from '@/lib/slices/study-slice';
import { AssistivContent } from 'study-types';

import Request from './request-service';
import ActiveStudyCacheService from '@/services/storage/active-study-cache-service';
import UtilityService from './utility-service';

const AuroraUtilityService = {
  // Function for generating assistiv content from prompt and setting state in study slice
  async generateAssistivContent(passedPrompt: string, dispatch: Dispatch<UnknownAction>) {
    // Set loading state
    dispatch(setIsStudyMaterialLoading(true));

    // Prepare request body
    const topicsInput = passedPrompt;
    const payload = { topics: topicsInput };

    // Reset study slice state to indicate a new search
    dispatch(setCurrentPrompt(topicsInput));
    dispatch(setSavedID(null));
    dispatch(setSetName(null));
    dispatch(setActiveAssistivTab('content')); // Default to content tab

    try {
      // Generate content from endpoint
      const res = await Request.post('sources/research', payload, true);
      const body = await res?.json();
      if (res && res.status === 429) {
        const msg =
          body?.error === 'limit_reached'
            ? `Monthly assistiv AI token limit reached for this subdomain.`
            : 'Monthly assistiv AI token limit reached.';
        toast.error(msg);
        dispatch(setIsStudyMaterialLoading(false));
        return;
      }
      if (!res || !body) {
        toast.error('Error while communicating with the server, please try again later');
        dispatch(setIsStudyMaterialLoading(false));
        return;
      }

      // Initialize assistiv content with API response
      const assistivContent: AssistivContent = {
        generatedContent: UtilityService.stripMarkdownWrapper(body.generatedContent || ''),
        videos: body.videos || [],
        sources: body.sources || [],
      };

      dispatch(setAssistivContent(assistivContent));
      dispatch(setIsStudyMaterialLoading(false));
    } catch (error) {
      console.error('Error generating assistiv content:', error);
      toast.error('Failed to generate content. Please try again.');
      dispatch(setIsStudyMaterialLoading(false));
    }
  },

  // Function for generating 20 new flashcards from prompt and setting state in study slice
  async generateFlashcards(passedPrompt: string, dispatch: Dispatch<UnknownAction>) {
    // Set loading state
    dispatch(setIsStudyMaterialLoading(true));

    // Prepare request body
    const topicsInput = passedPrompt;
    const payload = { topics: topicsInput, count: 20 };

    // Reset study slice state to indicate a new set
    dispatch(setCurrentPrompt(topicsInput));
    dispatch(setSavedID(null));
    dispatch(setSetName(null));

    try {
      // Generate cards from endpoint
      const res = await Request.post('study/flashcard', payload, true);
      const body = await res?.json();
      if (res && res.status === 429) {
        const msg =
          body?.error === 'limit_reached'
            ? `Monthly study flashcard AI token limit reached for this subdomain.`
            : 'Monthly study flashcard AI token limit reached.';
        toast.error(msg);
        return;
      }
      if (!res || !body || !Array.isArray(body)) {
        toast.error('Error while communicating with the server, please try again later');
        return;
      }

      // Initialize study session with generated flashcards
      dispatch(setStudyFlashcards(body));
      dispatch(initializeFavorites(body.length));

      // Set up cache for session persistence
      const studyCookie = {
        flashcards: body,
        favorited: new Array(body.length).fill(false),
        setName: null,
        savedID: null,
        currentPrompt: topicsInput,
        index: 0,
        completed: false,
      };
      ActiveStudyCacheService.setStudyStateCookie(studyCookie);
    } catch (error) {
      toast.error('Error while communicating with the server, please try again later');
    } finally {
      // End loading state
      dispatch(setIsStudyMaterialLoading(false));
    }
  },

  // Function for generating 10 new study questions from prompt and setting state in study slice
  async generateStudyQuiz(
    passedPrompt: string,
    dispatch: Dispatch<UnknownAction>,
    activityName?: string,
  ) {
    // Set loading state
    dispatch(setIsStudyMaterialLoading(true));

    // Prepare request body
    const topicsInput = passedPrompt;
    const payload = { topics: topicsInput, count: 10 };

    // Reset study slice state to indicate a new set
    dispatch(setCurrentPrompt(topicsInput));
    dispatch(setSavedID(null));
    dispatch(setSetName(activityName || null));

    try {
      // Generate cards from endpoint
      const res = await Request.post('study/quiz', payload, true);
      const body = await res?.json();
      if (res && res.status === 429) {
        const msg =
          body?.error === 'limit_reached'
            ? `Monthly study quiz AI token limit reached for this subdomain.`
            : 'Monthly study quiz AI token limit reached.';
        toast.error(msg);
        return;
      }
      if (!res || !body || !Array.isArray(body)) {
        toast.error('Error while communicating with the server, please try again later');
        return;
      }

      // Initialize study quiz state with generated questions
      dispatch(setStudyQuestions(body));
      dispatch(initializeFavorites(body.length));
      dispatch(setQuizResults(new Array(body.length).fill(null)));

      // Set up cache for study quiz state persistence
      const studyCookie = {
        selfStudyQuestions: body,
        favorited: new Array(body.length).fill(false),
        setName: activityName || null,
        savedID: null,
        currentPrompt: topicsInput,
        index: 0,
        completed: false,
      };
      ActiveStudyCacheService.setStudyStateCookie(studyCookie);
      ActiveStudyCacheService.resetStudyQuizStateCookie();
    } catch (error) {
      toast.error('Error while communicating with the server, please try again later');
    } finally {
      // End loading state
      dispatch(setIsStudyMaterialLoading(false));
    }
  },

  // Function to continue assistiv flow conversation and append content
  async continueAssistivContent(
    passedPrompt: string,
    previousMarkdown: string,
    dispatch: Dispatch<UnknownAction>,
  ) {
    dispatch(setIsStudyMaterialLoading(true));
    try {
      const payload = { previous_markdown: previousMarkdown, prompt: passedPrompt };
      const res = await Request.post('sources/research/continue', payload, true);
      const body = await res?.json();
      if (res && res.status === 429) {
        const msg =
          body?.error === 'limit_reached'
            ? `Monthly assistiv AI token limit reached for this subdomain.`
            : 'Monthly assistiv AI token limit reached.';
        toast.error(msg);
        return;
      }
      if (!res || !body) {
        toast.error('Error while communicating with the server, please try again later');
        return;
      }
      dispatch(
        appendAssistivContent({
          markdown: UtilityService.stripMarkdownWrapper(body.markdown || ''),
          sources: body.sources || [],
        }),
      );
    } catch (error) {
      console.error('Error continuing assistiv content:', error);
      toast.error('Failed to generate follow-up. Please try again.');
    } finally {
      dispatch(setIsStudyMaterialLoading(false));
    }
  },

  // Function for generating quiz questions from course documents
  async generateDocumentQuiz(
    docId: string,
    subunitId: string | undefined,
    unitId: string | undefined,
    dispatch: Dispatch<UnknownAction>,
    activityName?: string,
  ) {
    // Set loading state
    dispatch(setIsStudyMaterialLoading(true));

    // Reset study slice state to indicate a new set
    dispatch(setCurrentPrompt(activityName || 'Document Quiz'));
    dispatch(setSavedID(null));
    dispatch(setSetName(activityName || null));

    try {
      // First, fetch the document from assistiv backend
      const docPayload = {
        docId,
        subunitId: subunitId === '' ? undefined : subunitId,
        unitId: subunitId && subunitId !== '' ? undefined : unitId,
      };

      const docRes = await Request.post('content/doc/view', docPayload);
      if (!docRes || !docRes.ok) {
        toast.error('Failed to fetch document from server');
        return;
      }

      // Check if it's a PDF (binary) or markdown (JSON)
      const contentType = docRes.headers.get('content-type');
      let documentFile: File;
      let documentUrl: string;

      if (contentType?.includes('application/pdf')) {
        // It's a PDF - convert to File object
        const pdfBlob = await docRes.blob();
        documentFile = new File([pdfBlob], `${activityName || 'document'}.pdf`, {
          type: 'application/pdf',
        });
        documentUrl = URL.createObjectURL(pdfBlob);
      } else {
        // It's markdown content
        const docData = await docRes.json();
        if (docData.error || !docData.content) {
          toast.error('Document content not available');
          return;
        }
        // Convert markdown to a text file for processing
        const textBlob = new Blob([docData.content], { type: 'text/markdown' });
        documentFile = new File([textBlob], `${activityName || 'document'}.md`, {
          type: 'text/markdown',
        });
        documentUrl = URL.createObjectURL(textBlob);
      }

      // Store document info in state (same as uploaded documents)
      dispatch(setUploadedDocument(documentFile));
      dispatch(setDocumentUrl(documentUrl));

      // Store the original document ID for future use (saving quizzes)
      dispatch(setCourseDocumentId(docId));

      // Now use the same upload quiz endpoint as user uploads
      await this.generateUploadQuiz(documentFile, dispatch, 10);
    } catch (error) {
      console.error('Document quiz error:', error);
      toast.error(
        `Failed to generate quiz: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      // End loading state
      dispatch(setIsStudyMaterialLoading(false));
    }
  },

  // Function for generating quiz questions from uploaded PDF file
  async generateUploadQuiz(file: File, dispatch: Dispatch<UnknownAction>, count: number = 10) {
    // Set loading state
    dispatch(setIsStudyMaterialLoading(true));

    // Store the uploaded document in state
    dispatch(setUploadedDocument(file));

    // Create object URL for document viewing
    const documentUrl = URL.createObjectURL(file);
    dispatch(setDocumentUrl(documentUrl));

    // Reset study slice state to indicate a new set
    dispatch(setCurrentPrompt(`Quiz from ${file.name}`));
    dispatch(setSavedID(null));
    dispatch(setSetName(`${file.name} Quiz`));

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('document', file);
      formData.append('count', count.toString());

      // Generate quiz from uploaded file endpoint
      const res = await Request.post('study/upload-quiz', formData, true);
      const body = await res?.json();

      if (res && res.status === 429) {
        const msg =
          body?.error === 'limit_reached'
            ? `Monthly study quiz AI token limit reached for this subdomain.`
            : 'Monthly study quiz AI token limit reached.';
        toast.error(msg);
        return;
      }
      if (!res) {
        toast.error('Failed to connect to Aurora server. Please check if Aurora is running.');
        return;
      }
      if (!body) {
        toast.error(`Server error: ${res.status} ${res.statusText}`);
        return;
      }
      if (body.error) {
        toast.error(`Upload error: ${body.error}`);
        return;
      }
      if (!Array.isArray(body)) {
        toast.error(`Unexpected response format: ${JSON.stringify(body)}`);
        return;
      }

      // Initialize study quiz state with generated questions
      dispatch(setStudyQuestions(body));
      dispatch(initializeFavorites(body.length));
      dispatch(setQuizResults(new Array(body.length).fill(null)));

      // Set up cache for study quiz state persistence
      const studyCookie = {
        selfStudyQuestions: body,
        favorited: new Array(body.length).fill(false),
        setName: `${file.name} Quiz`,
        savedID: null,
        currentPrompt: `Quiz from ${file.name}`,
        index: 0,
        completed: false,
      };
      ActiveStudyCacheService.setStudyStateCookie(studyCookie);
      ActiveStudyCacheService.resetStudyQuizStateCookie();
    } catch (error) {
      console.error('Upload quiz error:', error);
      toast.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // End loading state
      dispatch(setIsStudyMaterialLoading(false));
    }
  },
};

export default AuroraUtilityService;
